import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, X, ArrowRight, Users, Repeat, CreditCard, Wallet } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const PRICE_PER_SQM = 0.5;
const PRICE_ROUND_TO = 5;
const MIN_QUOTE_AUD = 30;
const roundUpToFive = (n) => (n > 0 ? Math.ceil(n / PRICE_ROUND_TO) * PRICE_ROUND_TO : 0);

export default function BookingForm() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [areas, setAreas] = useState([{ label: "Front lawn", length: 10, width: 6 }]);
  const [address, setAddress] = useState("");
  const [scheduledDate, setScheduledDate] = useState("");
  const [notes, setNotes] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [submitting, setSubmitting] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);

  const totalArea = areas.reduce((s, a) => {
    if (a.area_sqm) return s + Number(a.area_sqm);
    return s + (Number(a.length) || 0) * (Number(a.width) || 0);
  }, 0);
  const roundedPrice = roundUpToFive(totalArea * PRICE_PER_SQM);
  const minimumApplied = totalArea > 0 && roundedPrice < MIN_QUOTE_AUD;
  const rawPrice = minimumApplied ? MIN_QUOTE_AUD : roundedPrice;
  const willDiscount = (user?.pending_discounts || 0) > 0 && rawPrice > 0;
  const estDiscount = willDiscount ? rawPrice * 0.10 : 0;
  const totalPrice = (rawPrice - estDiscount).toFixed(2);

  // Street schedule lookup (debounced) — find aligned dates from neighbors
  const [streetInfo, setStreetInfo] = useState({ neighbors: 0, suggested_dates: [] });
  useEffect(() => {
    if (!address || address.trim().length < 4) {
      setStreetInfo({ neighbors: 0, suggested_dates: [] });
      return;
    }
    const handle = setTimeout(async () => {
      try {
        const res = await api.get(`/street-schedule`, { params: { address } });
        setStreetInfo(res.data);
      } catch {
        setStreetInfo({ neighbors: 0, suggested_dates: [] });
      }
    }, 500);
    return () => clearTimeout(handle);
  }, [address]);

  const formatDate = (iso) => {
    try {
      const d = new Date(iso + "T00:00:00");
      return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
    } catch { return iso; }
  };

  const setArea = (idx, key, val) => {
    const next = [...areas];
    next[idx] = { ...next[idx], [key]: val };
    setAreas(next);
  };
  const roundDimensionOnBlur = (idx, key, val) => {
    const n = Math.ceil(Number(val) || 0);
    setArea(idx, key, n);
  };
  const addArea = () => setAreas([...areas, { label: `Area ${areas.length + 1}`, length: 0, width: 0 }]);
  const removeArea = (idx) => setAreas(areas.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.info("Please sign in to continue");
      navigate("/login", { state: { from: { pathname: "/book" } } });
      return;
    }
    if (totalArea <= 0) {
      toast.error("Please enter at least one lawn area");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        areas: areas.map((a) => ({ label: a.label, length: Number(a.length) || 0, width: Number(a.width) || 0 })),
        address,
        scheduled_date: scheduledDate,
        notes,
        payment_method: paymentMethod,
      };
      const bookingRes = await api.post("/bookings", payload);
      const booking = bookingRes.data;
      // Refresh user so pending_discounts count is in sync
      refresh && refresh();

      if (paymentMethod === "cash") {
        toast.success("Booking confirmed — pay cash to the crew on the day.");
        navigate("/dashboard");
        return;
      }

      // Card: create Stripe checkout session and redirect
      const checkoutRes = await api.post(`/bookings/${booking.id}/checkout`, {
        origin_url: window.location.origin,
      });
      window.location.href = checkoutRes.data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout");
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-12 py-16">
      <div className="label-overline mb-3">— New booking</div>
      <h1 className="font-serif text-4xl lg:text-5xl text-deep-forest tracking-tight">Tell us about your lawn.</h1>
      <p className="text-deep-forest/70 mt-3 max-w-xl">Add every patch. We&apos;ll tally the square meters and show you an instant total.</p>

      <form onSubmit={submit} className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10" data-testid="booking-form">
        <div className="lg:col-span-7">
          <div className="bg-bone-white border border-deep-forest/10 p-8">
            <div className="flex items-center justify-between">
              <div className="label-overline">Lawn areas</div>
              <button type="button" data-testid="booking-add-area-btn" onClick={addArea} className="text-sm flex items-center gap-2 px-4 py-2 border-2 border-[#2F6840] bg-[#2F6840]/10 text-[#2F6840] hover:bg-[#2F6840] hover:text-warm-sand transition-colors font-semibold">
                <Plus className="w-4 h-4" /> Add area
              </button>
            </div>

            <p className="text-xs text-deep-forest/55 mt-3 italic" data-testid="booking-measure-tip">
              💡 Tip: measure or walk the longest length and widest width of each lawn area to get an estimate.
            </p>

            <div className="mt-6 space-y-6">
              {areas.map((a, i) => (
                <div key={i} className="border-t border-deep-forest/10 pt-6" data-testid={`booking-area-${i}`}>
                  <div className="grid grid-cols-12 gap-4 items-end">
                    <div className="col-span-12 sm:col-span-4">
                      <label className="text-xs uppercase tracking-widest text-deep-forest/60">Label</label>
                      <input data-testid={`booking-label-${i}`} value={a.label || ""} onChange={(e) => setArea(i, "label", e.target.value)} className="field-line" />
                    </div>
                    <div className="col-span-5 sm:col-span-3">
                      <label className="text-xs uppercase tracking-widest text-deep-forest/60">Length (m)</label>
                      <input data-testid={`booking-length-${i}`} type="number" min="0" step="1" value={a.length} onChange={(e) => setArea(i, "length", e.target.value)} onBlur={(e) => roundDimensionOnBlur(i, "length", e.target.value)} className="field-line" />
                    </div>
                    <div className="col-span-5 sm:col-span-3">
                      <label className="text-xs uppercase tracking-widest text-deep-forest/60">Width (m)</label>
                      <input data-testid={`booking-width-${i}`} type="number" min="0" step="1" value={a.width} onChange={(e) => setArea(i, "width", e.target.value)} onBlur={(e) => roundDimensionOnBlur(i, "width", e.target.value)} className="field-line" />
                    </div>
                    <div className="col-span-2 sm:col-span-2 flex items-end gap-2 justify-end">
                      <div className="text-right">
                        <div className="text-xs uppercase tracking-widest text-deep-forest/60">m²</div>
                        <div className="font-serif text-xl text-deep-forest" data-testid={`booking-area-sqm-${i}`}>{((Number(a.length) || 0) * (Number(a.width) || 0)).toFixed(1)}</div>
                      </div>
                      {areas.length > 1 && (
                        <button type="button" data-testid={`booking-remove-${i}`} onClick={() => removeArea(i)} className="p-2 text-deep-forest/40 hover:text-terracotta">
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-bone-white border border-deep-forest/10 p-8 mt-6">
            <div className="label-overline">Where &amp; when</div>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="sm:col-span-2">
                <label className="text-xs uppercase tracking-widest text-deep-forest/60">Service address</label>
                <input data-testid="booking-address-input" required value={address} onChange={(e) => setAddress(e.target.value)} className="field-line" placeholder="123 Maple Street, Springfield" />
              </div>

              {streetInfo.neighbors > 0 && (
                <div className="sm:col-span-2 bg-status-scheduled-bg/60 border border-status-scheduled-text/20 p-5" data-testid="street-schedule-panel">
                  <div className="flex items-center gap-2 label-overline text-status-scheduled-text">
                    <Users className="w-3.5 h-3.5" /> Your street is already on the route
                  </div>
                  <p className="text-sm text-deep-forest/80 mt-2">
                    {streetInfo.neighbors} {streetInfo.neighbors === 1 ? "neighbour" : "neighbours"} already mowing with us. Align with their 3-week cycle so we can do your lawn the same day:
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2" data-testid="street-suggested-dates">
                    {streetInfo.suggested_dates.map((d) => (
                      <button
                        key={d}
                        type="button"
                        onClick={() => setScheduledDate(d)}
                        data-testid={`street-date-${d}`}
                        className={`text-sm px-3 py-2 border transition-colors ${scheduledDate === d ? "bg-deep-forest text-warm-sand border-deep-forest" : "bg-bone-white border-deep-forest/20 text-deep-forest hover:border-terracotta hover:text-terracotta"}`}
                      >
                        {formatDate(d)}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <label className="text-xs uppercase tracking-widest text-deep-forest/60">Start date</label>
                <input data-testid="booking-date-input" required type="date" value={scheduledDate} onChange={(e) => setScheduledDate(e.target.value)} className="field-line" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-deep-forest/60">Notes (optional)</label>
                <input data-testid="booking-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} className="field-line" placeholder="Gate code, dog warning…" />
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="bg-deep-forest text-warm-sand p-8 sticky top-24">
            <div className="label-overline text-warm-sand/70">— Estimate</div>
            <div className="mt-6 space-y-4">
              {areas.map((a, i) => (
                <div key={i} className="flex justify-between text-sm">
                  <span className="text-warm-sand/70">{a.label || `Area ${i + 1}`} <span className="text-warm-sand/40">({a.length}×{a.width})</span></span>
                  <span className="text-warm-sand">{((Number(a.length) || 0) * (Number(a.width) || 0)).toFixed(1)} m²</span>
                </div>
              ))}
            </div>
            <div className="my-6 h-px bg-warm-sand/15" />
            {willDiscount && (
              <div className="mb-4 flex justify-between text-sm" data-testid="booking-discount-line">
                <span className="text-warm-sand/70">Neighbour referral — 10% off</span>
                <span className="text-terracotta">− ${estDiscount.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between items-end">
              <div>
                <div className="label-overline text-warm-sand/70">Total area</div>
                <div className="font-serif text-3xl" data-testid="booking-total-area">{totalArea} m²</div>
              </div>
              <div className="text-right">
                <div className="label-overline text-warm-sand/70">Price</div>
                <div className="font-serif text-5xl text-terracotta" data-testid="booking-total-price">${totalPrice}<span className="text-sm text-warm-sand/60 align-top ml-1 font-sans">AUD</span></div>
              </div>
            </div>
            <p className="text-xs text-warm-sand/55 mt-4 italic" data-testid="booking-rounding-note">
              * Lengths are rounded up to the next whole metre for edging, and the final price is rounded up to the nearest $5.
            </p>
            {minimumApplied && (
              <div
                data-testid="booking-minimum-notice"
                className="mt-4 flex gap-3 bg-warm-sand/10 border-l-2 border-terracotta px-4 py-3"
              >
                <span className="font-serif text-terracotta text-lg leading-none mt-0.5">$30</span>
                <p className="text-xs text-warm-sand/85 leading-relaxed">
                  A <strong className="text-warm-sand">$30 minimum charge</strong> has been applied. Your lawn works out to <strong className="text-warm-sand">${roundedPrice.toFixed(2)}</strong>, but we can&apos;t send a crew out for less than $30 — cost of fuel, kit and travel.
                </p>
              </div>
            )}
            <p
              className="mt-3 text-xs text-warm-sand/60 leading-relaxed"
              data-testid="booking-verify-onsite-notice"
            >
              <strong className="text-warm-sand/80">Fair pricing promise.</strong> All new sites are measured by the crew on the first visit — if there&apos;s a discrepancy with your estimate, we&apos;ll adjust the cost (up or down) before the next mow.
            </p>

            <div className="mt-8">
              <div className="label-overline text-warm-sand/70 mb-3">How would you like to pay?</div>
              <div className="grid grid-cols-1 gap-2" data-testid="payment-method-selector">
                <button
                  type="button"
                  onClick={() => setPaymentMethod("card")}
                  data-testid="payment-method-card"
                  className={`text-left p-4 border transition-colors flex items-start gap-3 ${paymentMethod === "card" ? "border-terracotta bg-terracotta/10" : "border-warm-sand/20 hover:border-warm-sand/40"}`}
                >
                  <CreditCard className={`w-5 h-5 mt-0.5 flex-shrink-0 ${paymentMethod === "card" ? "text-terracotta" : "text-warm-sand/70"}`} />
                  <div>
                    <div className="font-medium text-warm-sand">Pay by card now</div>
                    <div className="text-xs text-warm-sand/60 mt-0.5">Secured by Stripe. Booking is locked in instantly.</div>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setPaymentMethod("cash")}
                  data-testid="payment-method-cash"
                  className={`text-left p-4 border transition-colors flex items-start gap-3 ${paymentMethod === "cash" ? "border-terracotta bg-terracotta/10" : "border-warm-sand/20 hover:border-warm-sand/40"}`}
                >
                  <Wallet className={`w-5 h-5 mt-0.5 flex-shrink-0 ${paymentMethod === "cash" ? "text-terracotta" : "text-warm-sand/70"}`} />
                  <div>
                    <div className="font-medium text-warm-sand">Pay cash on the day</div>
                    <div className="text-xs text-warm-sand/60 mt-0.5">Hand the crew ${totalPrice} AUD when they arrive.</div>
                  </div>
                </button>
              </div>
            </div>

            <button type="submit" data-testid="booking-submit-btn" disabled={submitting} className="btn-primary w-full justify-center mt-6">
              {submitting ? (paymentMethod === "cash" ? "Confirming…" : "Redirecting to payment…") : (
                paymentMethod === "cash"
                  ? <>Confirm booking <ArrowRight className="w-4 h-4" /></>
                  : <>Continue to payment <ArrowRight className="w-4 h-4" /></>
              )}
            </button>
            <div className="mt-5 flex items-start gap-2 text-warm-sand/70 text-xs" data-testid="booking-recurring-note">
              <Repeat className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span>Recurring service — ${totalPrice} AUD {paymentMethod === "cash" ? "due to the crew" : "charged"} every 3 weeks. Pause or cancel anytime from your dashboard.</span>
            </div>
            <p className="text-warm-sand/40 text-xs mt-2">
              {paymentMethod === "card" ? "Payments are processed securely by Stripe." : "No card details required — pay cash on each mow."}
            </p>
          </div>
        </div>
      </form>
    </div>
  );
}
